import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Settings, Package, Upload, FileText, Trash2, Download, TrendingUp, ExternalLink,
  MessageSquare, Send, History, Paperclip, X as XIcon, Clock, ArrowUpDown,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return iso; }
};

const InventoryManagerEmbed = ({ isDark }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [dealer, setDealer] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');

  // Uploaded inventory docs state
  const [docs, setDocs] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Extracted-items grid state (inline, replaces the removed CW iframe)
  const [items, setItems] = useState([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsQuery, setItemsQuery] = useState('');
  const [itemsLoading, setItemsLoading] = useState(false);
  // When Inventory Watch chat discusses specific units, the inventory
  // section below populates with exactly those (clearable banner).
  const [watchFilter, setWatchFilter] = useState(null);
  const [dedupeBusy, setDedupeBusy] = useState(false);
  // Inventory Watch A.I. — surfaces what changed since the last snapshot
  // + hero units to focus on. Fetched lazily when the dealer opens the
  // Inventory tab so we don't burn tokens on every page load.
  const [watch, setWatch] = useState(null);
  const [watchLoading, setWatchLoading] = useState(false);
  // Inventory Watch chat — advisor tunes hero picks / asks about inventory
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatImages, setChatImages] = useState([]);
  const chatEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const [daysSort, setDaysSort] = useState(null);

  const cycleDaysSort = () => setDaysSort((s) => (s === 'desc' ? 'asc' : s === 'asc' ? null : 'desc'));
  const sortRows = (rows) => {
    if (!daysSort) return rows;
    return [...rows].sort((a, b) => {
      const va = a.days_on_lot, vb = b.days_on_lot;
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return daysSort === 'desc' ? vb - va : va - vb;
    });
  };

  const attachChatImage = (e) => {
    Array.from(e.target.files || []).slice(0, 3 - chatImages.length).forEach((file) => {
      if (!file.type.startsWith('image/')) return toast.error('Images only (screenshots, photos)');
      if (file.size > 10 * 1024 * 1024) return toast.error(`${file.name} is over 10 MB`);
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const MAX = 1800;
          const scale = Math.min(1, MAX / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          setChatImages((prev) => [...prev, { name: file.name, dataUrl: canvas.toDataURL('image/jpeg', 0.88) }]);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };
  // Correction log — audit trail of chat/reconciliation inventory corrections
  const [logOpen, setLogOpen] = useState(false);
  const [logEntries, setLogEntries] = useState(null);

  const toggleLog = async () => {
    const next = !logOpen;
    setLogOpen(next);
    if (next) {
      try {
        const r = await axios.get(`${API}/dealer/inventory-corrections`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        setLogEntries(r.data.corrections || []);
      } catch {
        setLogEntries([]);
      }
    }
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if ((!msg && chatImages.length === 0) || chatBusy) return;
    const imgs = chatImages.map((i) => i.dataUrl);
    const nextMsgs = [...chatMessages, { role: 'user', content: msg || 'See the attached screenshot.', images: imgs }];
    setChatMessages(nextMsgs);
    setChatInput('');
    setChatImages([]);
    setChatBusy(true);
    try {
      const token = getAuthToken();
      const r = await axios.post(
        `${API}/dealer/inventory-watch/chat`,
        {
          message: msg,
          images: imgs,
          history: chatMessages.slice(-12).map(({ role, content }) => ({ role, content })),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setChatMessages((prev) => [...prev, { role: 'assistant', content: r.data.reply }]);
      if (r.data.matched_items && r.data.matched_items.length > 0) {
        setWatchFilter({ label: msg, items: r.data.matched_items });
        toast.success(`${r.data.matched_items.length} matching unit${r.data.matched_items.length === 1 ? '' : 's'} now showing in the inventory list below`);
      }
      if (r.data.applied && r.data.applied.length > 0) {
        r.data.applied.forEach((u) => {
          toast.success(`Inventory corrected: ${u.unit} (#${u.stock})${u.status ? ` → ${u.status.replace('_', ' ')}` : ''}`);
        });
        fetchItems(itemsQuery);
      }
      if (r.data.hero_units && r.data.hero_units.length > 0) {
        setWatch((prev) => (prev ? { ...prev, hero_units: r.data.hero_units } : prev));
        toast.success('Hero picks updated');
      }
    } catch {
      setChatMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry — I could not reach the AI just now. Try again in a moment.' }]);
    } finally {
      setChatBusy(false);
    }
  };

  useEffect(() => {
    // Scroll ONLY the chat message container — scrollIntoView scrolled
    // every ancestor including the page itself (screen-jump bug).
    const el = chatScrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [chatMessages, chatBusy]);

  const fetchWatch = async () => {
    setWatchLoading(true);
    try {
      const token = getAuthToken();
      const r = await axios.get(`${API}/dealer/inventory-watch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWatch(r.data);
    } catch {
      setWatch({ has_watch: false });
    } finally {
      setWatchLoading(false);
    }
  };

  const fetchItems = async (query = '') => {
    setItemsLoading(true);
    try {
      const token = getAuthToken();
      const params = new URLSearchParams({ q: query, limit: '200' });
      const res = await axios.get(`${API}/dealer/inventory-items?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
      setItemsTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Could not load inventory list');
    } finally {
      setItemsLoading(false);
    }
  };

  const runDedupe = async () => {
    setDedupeBusy(true);
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API}/dealer/inventory-items/dedupe`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const removed = res.data.duplicates_removed || 0;
      const kept = res.data.unique_kept || 0;
      if (removed > 0) {
        toast.success(`Removed ${removed} duplicate${removed === 1 ? '' : 's'} · ${kept} unique units remain`);
      } else {
        toast.success(`No duplicates found · ${kept} unique units`);
      }
      await fetchItems(itemsQuery);
      await fetchDocs();
    } catch {
      toast.error('Dedupe failed');
    } finally {
      setDedupeBusy(false);
    }
  };

  useEffect(() => {
    fetchDealer();
    fetchDocs();
    fetchItems('');
    fetchWatch();
  }, []);

  // Re-fetch the extracted-items grid + AI watch whenever the total
  // active count changes (dedupe just ran, a new PDF finished
  // extracting, a doc was deleted, etc.).
  useEffect(() => {
    fetchItems(itemsQuery);
    fetchWatch();
  }, [totalItems]);

  // Auto-poll while any doc is still in the "extracting" state. Covers the
  // case where the dealer refreshes / re-opens this tab mid-extraction and
  // the per-upload polling loop from handleFileSelected has been torn down.
  useEffect(() => {
    const stillExtracting = (docs || []).some((d) => d.processing_status === 'extracting');
    if (!stillExtracting) return undefined;
    const t = setInterval(fetchDocs, 5000);
    return () => clearInterval(t);
  }, [docs]);

  const fetchDealer = async () => {
    const token = getAuthToken();
    try {
      const res = await axios.get(`${API}/dealer/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDealer(res.data);
      setExternalUrl(res.data.external_inventory_url || '');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load inventory settings');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDocs = async () => {
    const token = getAuthToken();
    try {
      const res = await axios.get(`${API}/dealer/inventory-docs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocs(res.data.docs || []);
      setTotalItems(res.data.total_active_items || 0);
    } catch (err) {
      // Silent — endpoint may not exist yet on old dealer accounts
    }
  };

  const saveExternalUrl = async () => {
    const token = getAuthToken();
    try {
      await axios.put(`${API}/dealer/profile`,
        { external_inventory_url: externalUrl },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDealer({ ...dealer, external_inventory_url: externalUrl });
      setShowSettingsModal(false);
      toast.success('Inventory link saved!');
    } catch (error) {
      toast.error('Failed to save link');
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File is larger than 50MB — please split into smaller uploads.');
      return;
    }
    const token = getAuthToken();
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API}/dealer/inventory-docs/upload`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const status = res.data?.processing_status;
      const docId = res.data?.doc_id;
      if (status === 'extracting') {
        toast.success(`${file.name} uploaded — AI is extracting inventory items in the background.`);
        // Poll every 5s for up to 15 minutes. Large multi-page PDFs
        // routinely take 3-8 minutes because each page hits Gemini
        // sequentially — we surface live progress from the backend
        // (pages_processed / pages_total) so the dealer never wonders
        // whether it's stuck. Self-heals against transient fetch errors.
        let elapsed = 0;
        const MAX_ELAPSED_SEC = 900; // 15 min
        const poll = async () => {
          if (elapsed >= MAX_ELAPSED_SEC) {
            toast.error(`${file.name}: still extracting after 15 min. Refresh the page — the retry button will appear if it fails.`);
            return;
          }
          elapsed += 5;
          try {
            const t = getAuthToken();
            const listRes = await axios.get(`${API}/dealer/inventory-docs`, {
              headers: { Authorization: `Bearer ${t}` },
            });
            setDocs(listRes.data.docs || []);
            setTotalItems(listRes.data.total_active_items || 0);
            const row = (listRes.data.docs || []).find((d) => d.id === docId);
            if (!row || row.processing_status === 'extracting') {
              setTimeout(poll, 5000);
            } else if (row.processing_status === 'extraction_failed') {
              toast.error(`Extraction failed for ${file.name}: ${row.extraction_error || 'Unknown error'}. Tap the retry button to try again.`);
            } else {
              const r = row.reconciliation || {};
              const parts = [];
              if (r.added) parts.push(`+${r.added} added`);
              if (r.removed) parts.push(`-${r.removed} removed`);
              if (r.price_changed) parts.push(`${r.price_changed} price change${r.price_changed === 1 ? '' : 's'}`);
              if (r.corrections_preserved) parts.push(`${r.corrections_preserved} floor correction${r.corrections_preserved === 1 ? '' : 's'} preserved`);
              const summary = parts.length ? parts.join(' \u00B7 ') : `${row.extracted_count} item${row.extracted_count === 1 ? '' : 's'} extracted`;
              toast.success(`${file.name}: ${summary}`);
            }
          } catch {
            setTimeout(poll, 5000);
          }
        };
        setTimeout(poll, 5000);
      } else if (status === 'no_extraction') {
        toast.success(`${file.name} saved — no supported AI extractor for this format.`);
      } else {
        const r = res.data.reconciliation || {};
        const parts = [];
        if (r.added) parts.push(`+${r.added} added`);
        if (r.removed) parts.push(`-${r.removed} removed`);
        if (r.price_changed) parts.push(`${r.price_changed} price change${r.price_changed === 1 ? '' : 's'}`);
        if (r.corrections_preserved) parts.push(`${r.corrections_preserved} floor correction${r.corrections_preserved === 1 ? '' : 's'} preserved`);
        const summary = parts.length ? parts.join(' · ') : `${res.data.extracted} item${res.data.extracted === 1 ? '' : 's'} extracted`;
        toast.success(`${file.name}: ${summary}`);
      }
      fetchDocs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId, filename) => {
    if (!window.confirm(`Remove "${filename}" from your inventory? Items from this file will no longer surface as local matches.`)) return;
    const token = getAuthToken();
    try {
      await axios.delete(`${API}/dealer/inventory-docs/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Removed');
      fetchDocs();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className={`animate-spin ${isDark ? 'text-blue-400' : 'text-blue-600'}`} size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-3 flex flex-col h-full min-h-[600px]" data-testid="inventory-manager-embed">
      {/* Header — single compact row on desktop, wraps on mobile */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0 flex items-center gap-3 flex-wrap">
          <h2 className={`text-lg sm:text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Package size={20} />
            Inventory
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-800'}`}
              data-testid="inventory-active-chip"
            >
              <TrendingUp size={10} /> {totalItems} active
            </span>
          </h2>
          {dealer?.external_inventory_url ? (
            <a
              href={dealer.external_inventory_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 text-[11px] font-semibold hover:underline ${isDark ? 'text-blue-300' : 'text-blue-600'}`}
              data-testid="inventory-public-site-link"
            >
              View our public site <ExternalLink size={11} />
            </a>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelected}
            accept=".pdf,.csv,.xlsx,.xls,.docx,.doc,.txt,.jpg,.jpeg,.png,.webp"
            data-testid="inventory-upload-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            size="sm"
            className="flex items-center gap-1.5 bg-[#F2A900] hover:bg-amber-400 text-black font-bold h-8 px-3"
            data-testid="inventory-upload-btn"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            <span className="hidden sm:inline">{uploading ? 'Uploading…' : 'Upload File'}</span>
            <span className="sm:hidden">{uploading ? '…' : 'Upload'}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettingsModal(true)}
            className="h-8 px-2"
            aria-label="Inventory settings"
            data-testid="inventory-settings-btn"
          >
            <Settings size={14} />
          </Button>
        </div>
      </div>

      {/* Compact uploaded-files strip — capped height so it never crowds
          the embedded inventory iframe below. Scrolls when there are
          many docs, and collapses to a slim empty-state on first use. */}
      <section
        className={`rounded-xl border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}
        data-testid="inventory-uploaded-section"
      >
        {docs.length === 0 ? (
          <div className={`px-4 py-3 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`} data-testid="inventory-docs-empty">
            No inventory files yet. Upload a CSV, XLSX, or PDF — the AI reads every RV inside. Files stay private.
          </div>
        ) : (
          <div className="max-h-[168px] overflow-y-auto divide-y divide-slate-200/60 dark:divide-slate-700/60" data-testid="inventory-docs-list">
            {docs.map((d) => {
              const r = d.reconciliation || {};
              return (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-2 px-3 py-2"
                  data-testid={`inventory-doc-${d.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={14} className={`shrink-0 ${isDark ? 'text-blue-300' : 'text-blue-600'}`} />
                    <div className="min-w-0">
                      <p className={`text-xs sm:text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {d.original_filename}
                      </p>
                      <p className={`text-[11px] leading-tight ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {d.processing_status === 'extracting' ? (
                          <span className="inline-flex items-center gap-1 text-amber-500">
                            <Loader2 size={10} className="animate-spin" />
                            {d.pages_total && d.pages_total > 1
                              ? `Page ${(d.pages_processed || 0) + (d.pages_processed === d.pages_total ? 0 : 1)}/${d.pages_total}${d.extracted_count ? ` · ${d.extracted_count} RVs` : ''}`
                              : 'Extracting\u2026'}
                          </span>
                        ) : d.processing_status === 'extraction_failed' ? (
                          <span className="text-rose-500 font-semibold" title={d.extraction_error || ''}>
                            Failed &mdash; tap retry
                          </span>
                        ) : (
                          <>{d.extracted_count} RV{d.extracted_count === 1 ? '' : 's'}</>
                        )}
                        <span className="mx-1">·</span>{fmtBytes(d.size_bytes)}
                        <span className="hidden sm:inline"><span className="mx-1">·</span>{fmtDate(d.uploaded_at)}</span>
                        {d.uploaded_by_label && (
                          <span
                            className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${d.uploaded_by_label === 'you' ? (isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700') : (isDark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-700')}`}
                            data-testid={`inventory-doc-uploader-${d.id}`}
                          >
                            uploaded by {d.uploaded_by_label}
                          </span>
                        )}
                        {(r.added || r.removed || r.price_changed) ? (
                          <span className="ml-1.5">
                            {r.added ? <span className="text-emerald-500 font-semibold">+{r.added} </span> : null}
                            {r.removed ? <span className="text-rose-500 font-semibold">-{r.removed} </span> : null}
                            {r.price_changed ? <span className="text-amber-500 font-semibold">{r.price_changed}Δ</span> : null}
                          </span>
                        ) : null}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {(d.processing_status === 'extracting' || d.processing_status === 'extraction_failed') && (
                      <button
                        onClick={async () => {
                          try {
                            const t = getAuthToken();
                            await axios.post(
                              `${API}/dealer/inventory-docs/${d.id}/retry-extraction`,
                              {},
                              { headers: { Authorization: `Bearer ${t}` } },
                            );
                            toast.success('Retry started — refreshing.');
                            setTimeout(fetchDocs, 4000);
                            setTimeout(fetchDocs, 12000);
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Retry failed');
                          }
                        }}
                        className={`p-1.5 rounded ${isDark ? 'hover:bg-amber-500/10 text-amber-300' : 'hover:bg-amber-50 text-amber-600'}`}
                        title="Retry AI extraction"
                        data-testid={`inventory-doc-retry-${d.id}`}
                      >
                        <TrendingUp size={13} />
                      </button>
                    )}
                    <a
                      href={`${API}/dealer/inventory-docs/${d.id}/download`}
                      className={`p-1.5 rounded ${isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                      title="Download original"
                      onClick={async (e) => {
                        e.preventDefault();
                        try {
                          const token = getAuthToken();
                          const resp = await axios.get(`${API}/dealer/inventory-docs/${d.id}/download`, {
                            headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
                          });
                          const url = URL.createObjectURL(resp.data);
                          const a = document.createElement('a');
                          a.href = url; a.download = d.original_filename; a.click();
                          setTimeout(() => URL.revokeObjectURL(url), 500);
                        } catch { toast.error('Download failed'); }
                      }}
                      data-testid={`inventory-doc-download-${d.id}`}
                    >
                      <Download size={13} />
                    </a>
                    <button
                      onClick={() => handleDelete(d.id, d.original_filename)}
                      className={`p-1.5 rounded ${isDark ? 'hover:bg-rose-500/10 text-rose-400' : 'hover:bg-rose-50 text-rose-500'}`}
                      title="Remove file"
                      data-testid={`inventory-doc-delete-${d.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Inventory Watch A.I. — surfaces what changed since the last
          upload + hero units the dealer should focus on for the week.
          Only renders once we have a completed upload to summarise. */}
      {watch?.has_watch && (
        <section
          className={`rounded-xl border p-3 sm:p-4 ${isDark ? 'bg-gradient-to-br from-indigo-500/10 to-purple-500/5 border-indigo-500/30' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200'}`}
          data-testid="inventory-watch-panel"
        >
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${isDark ? 'bg-indigo-500/30 text-indigo-200' : 'bg-indigo-200/70 text-indigo-800'}`}>
              <TrendingUp size={10} /> Inventory Watch A.I.
            </span>
            <span className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Snapshot from {watch.latest_doc?.filename || 'your latest upload'}
            </span>
          </div>
          <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {watch.headline}
          </p>
          {watch.changes && watch.changes.length > 0 ? (
            <ul className={`text-xs mt-1.5 space-y-0.5 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              {watch.changes.map((c, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-emerald-500">•</span> {c}
                </li>
              ))}
            </ul>
          ) : null}
          {watch.hero_units && watch.hero_units.length > 0 ? (
            <>
              <p className={`text-[10px] font-black uppercase tracking-widest mt-3 ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                Focus these first
              </p>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {watch.hero_units.slice(0, 4).map((u, i) => (
                  <div
                    key={i}
                    className={`text-xs rounded-lg px-2.5 py-1.5 ${isDark ? 'bg-slate-900/60 border border-slate-700' : 'bg-white/70 border border-slate-200'}`}
                    data-testid={`inventory-watch-hero-${i}`}
                  >
                    <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {u.year} {u.make} {u.model}
                      {u.stock ? <span className={`ml-1.5 text-[10px] font-mono ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>#{u.stock}</span> : null}
                    </div>
                    <div className={`text-[11px] leading-tight ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{u.why}</div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
          {watch.aging && watch.aging.count > 0 ? (
            <div
              className={`mt-3 rounded-lg border px-2.5 py-2 ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}
              data-testid="inventory-aging-watch"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                  <Clock size={10} /> Aging watch — sitting too long
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={async () => {
                      try {
                        const token = getAuthToken();
                        const r = await axios.post(`${API}/dealer/aging-digest/send-now`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        if (r.data?.sent) toast.success(`Aging report emailed — ${r.data.count} units included`);
                        else toast.info(r.data?.reason === 'no_aging_units' ? 'No units past 90 days — nothing to send' : 'No email on file for this account');
                      } catch { toast.error('Could not send the aging report'); }
                    }}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${isDark ? 'border-amber-500/40 text-amber-300 hover:bg-amber-500/15' : 'border-amber-300 text-amber-700 hover:bg-amber-100'}`}
                    data-testid="inventory-aging-email-btn"
                  >
                    Email me this report
                  </button>
                  <button
                    onClick={() => setWatchFilter({ label: 'Units 90+ days on lot', items: watch.aging.items || [] })}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-500/20 text-amber-200 hover:bg-amber-500/30' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}
                    data-testid="inventory-aging-see-all"
                  >
                    See all {watch.aging.count} in the list ↓
                  </button>
                </div>
              </div>
              <p className={`text-[11px] mt-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                {watch.aging.count} unit{watch.aging.count === 1 ? ' has' : 's have'} sat 90+ days on the lot. Longest:
              </p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {(watch.aging.items || []).slice(0, 3).map((u, i) => (
                  <span
                    key={i}
                    className={`text-[11px] rounded-md px-2 py-1 ${isDark ? 'bg-slate-900/60 border border-slate-700 text-slate-200' : 'bg-white/70 border border-slate-200 text-slate-800'}`}
                    data-testid={`inventory-aging-unit-${i}`}
                  >
                    <strong>{u.days_on_lot}d</strong> — {u.year} {u.make} {u.model}
                    {u.stock_number ? <span className="font-mono opacity-60"> #{u.stock_number}</span> : null}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {watchLoading ? <Loader2 className="animate-spin mt-2" size={12} /> : null}

          {/* Tune-the-picks chat — advisor talks to Inventory Watch A.I.
              to refocus hero units or ask about specific inventory. */}
          <div className="mt-3">
            <div className="flex flex-wrap items-center gap-2">
              {!chatOpen && (
                <button
                  onClick={() => setChatOpen(true)}
                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition-colors ${isDark ? 'border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/20' : 'border-indigo-300 text-indigo-700 hover:bg-indigo-100'}`}
                  data-testid="inventory-watch-chat-open"
                >
                  <MessageSquare size={12} /> Tune results — chat with Inventory Watch A.I.
                </button>
              )}
              <button
                onClick={toggleLog}
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition-colors ${isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-700/50' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                data-testid="inventory-correction-log-toggle"
              >
                <History size={12} /> Correction log
              </button>
            </div>

            {logOpen && (
              <div
                className={`mt-2 rounded-lg border px-3 py-2 ${isDark ? 'bg-slate-900/60 border-slate-700' : 'bg-white/80 border-slate-200'}`}
                data-testid="inventory-correction-log"
              >
                <p className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Correction log
                </p>
                {logEntries === null ? (
                  <Loader2 className="animate-spin" size={12} />
                ) : logEntries.length === 0 ? (
                  <p className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    No corrections yet — changes made through the chat will show up here.
                  </p>
                ) : (
                  <div className="max-h-40 overflow-y-auto divide-y divide-slate-500/10">
                    {logEntries.map((c) => (
                      <div key={c.id} className="py-1.5 flex flex-wrap items-baseline gap-x-2" data-testid={`correction-entry-${c.id}`}>
                        <span className={`text-[11px] font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                          {c.unit || c.stock}{c.stock ? ` (#${c.stock})` : ''}
                        </span>
                        <span className={`text-[11px] font-bold ${
                          c.status === 'available' ? 'text-emerald-500' : c.status === 'sold' ? 'text-rose-500' : 'text-amber-500'
                        }`}>
                          → {(c.status || '').replace('_', ' ')}{c.price ? ` @ $${Number(c.price).toLocaleString()}` : ''}
                        </span>
                        <span className={`text-[10px] ml-auto ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {c.source === 'reconciliation' ? 'auto-preserved' : (c.by || 'advisor')} · {new Date(c.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </span>
                        {c.note && <span className={`basis-full text-[10px] italic ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{c.note}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="inventory-quick-chips">
              {[
                {
                  key: 'oldest', label: 'Oldest on lot',
                  fn: () => [...items].filter((i) => i.days_on_lot != null).sort((a, b) => b.days_on_lot - a.days_on_lot).slice(0, 25),
                  lbl: '25 oldest by days on lot',
                },
                {
                  key: 'aging90', label: '90+ days',
                  fn: () => [...items].filter((i) => Number(i.days_on_lot) >= 90).sort((a, b) => b.days_on_lot - a.days_on_lot),
                  lbl: 'Units 90+ days on lot',
                },
                {
                  key: 'under25k', label: 'Under $25k',
                  fn: () => [...items].filter((i) => i.price && Number(i.price) < 25000).sort((a, b) => a.price - b.price),
                  lbl: 'Units under $25,000',
                },
                {
                  key: 'discount', label: 'Biggest discounts',
                  fn: () => [...items].filter((i) => i.list_price && i.price && Number(i.list_price) > Number(i.price)).sort((a, b) => (b.list_price - b.price) - (a.list_price - a.price)).slice(0, 25),
                  lbl: 'Top 25 discounts off list price',
                },
                {
                  key: 'pricecuts', label: 'Price cuts',
                  fn: () => [...items].filter((i) => i.previous_price && i.price && Number(i.previous_price) > Number(i.price)),
                  lbl: 'Units with recent price cuts',
                },
                {
                  key: 'longest', label: 'Longest',
                  fn: () => [...items].filter((i) => i.length_ft).sort((a, b) => b.length_ft - a.length_ft).slice(0, 25),
                  lbl: '25 longest units (ft)',
                  hide: !items.some((i) => i.length_ft),
                },
                {
                  key: 'priciest', label: 'Highest price',
                  fn: () => [...items].filter((i) => i.price).sort((a, b) => b.price - a.price).slice(0, 25),
                  lbl: 'Top 25 by price',
                },
              ].filter((c) => !c.hide).map((c) => {
                const active = watchFilter?.label === c.lbl;
                return (
                  <button
                    key={c.key}
                    onClick={() => (active ? setWatchFilter(null) : setWatchFilter({ label: c.lbl, items: c.fn() }))}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                      active
                        ? (isDark ? 'bg-indigo-500/25 border-indigo-400/60 text-indigo-200' : 'bg-indigo-100 border-indigo-300 text-indigo-800')
                        : (isDark ? 'border-slate-600 text-slate-300 hover:border-indigo-500/50 hover:text-indigo-300' : 'border-slate-300 text-slate-600 hover:border-indigo-300 hover:text-indigo-700')
                    }`}
                    data-testid={`inventory-quick-chip-${c.key}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>

            {chatOpen && (
              <div
                className={`rounded-lg border ${isDark ? 'bg-slate-900/60 border-slate-700' : 'bg-white/80 border-slate-200'}`}
                data-testid="inventory-watch-chat-panel"
              >
                <div className={`flex items-center justify-between px-2.5 py-1.5 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                    <MessageSquare size={10} /> Tune results
                  </span>
                  <button
                    onClick={() => setChatOpen(false)}
                    className={`text-[10px] font-semibold ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800'}`}
                    data-testid="inventory-watch-chat-close"
                  >
                    Hide
                  </button>
                </div>
                <div ref={chatScrollRef} className="max-h-48 overflow-y-auto px-2.5 py-2 space-y-1.5">
                  {chatMessages.length === 0 && (
                    <p className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Try: “Focus the picks on travel trailers under $30k” or “Which units had price cuts?” — or hit the paperclip to attach a screenshot (inventory sheet, listing, price tag) for the AI to read.
                    </p>
                  )}
                  {chatMessages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs leading-snug ${
                          m.role === 'user'
                            ? (isDark ? 'bg-indigo-500/30 text-indigo-100' : 'bg-indigo-100 text-indigo-900')
                            : (isDark ? 'bg-slate-800 text-slate-200 border border-slate-700' : 'bg-slate-100 text-slate-800')
                        }`}
                        data-testid={`inventory-watch-chat-msg-${i}`}
                      >
                        {m.images && m.images.length > 0 && (
                          <div className="flex gap-1 mb-1">
                            {m.images.map((src, j) => (
                              <img
                                key={j}
                                src={src}
                                alt="attachment"
                                className="h-16 w-16 object-cover rounded border border-white/20"
                                data-testid={`inventory-watch-chat-msg-${i}-img-${j}`}
                              />
                            ))}
                          </div>
                        )}
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {chatBusy && (
                    <div className="flex justify-start">
                      <div className={`rounded-lg px-2.5 py-1.5 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                        <Loader2 className="animate-spin" size={12} />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                {chatImages.length > 0 && (
                  <div className={`flex items-center gap-2 px-2 py-1.5 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`} data-testid="inventory-watch-chat-attachments">
                    {chatImages.map((img, i) => (
                      <div key={i} className="relative" data-testid={`inventory-watch-chat-attachment-${i}`}>
                        <img src={img.dataUrl} alt={img.name} className="h-12 w-12 object-cover rounded border border-slate-400/40" />
                        <button
                          onClick={() => setChatImages((prev) => prev.filter((_, j) => j !== i))}
                          className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full p-0.5"
                          data-testid={`inventory-watch-chat-attachment-remove-${i}`}
                          title={`Remove ${img.name}`}
                        >
                          <XIcon size={9} />
                        </button>
                      </div>
                    ))}
                    <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {chatImages.length}/3 attached — the AI will read the image{chatImages.length === 1 ? '' : 's'}
                    </span>
                  </div>
                )}
                <div className={`flex items-center gap-1.5 px-2 py-1.5 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <input
                    ref={chatFileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={attachChatImage}
                    className="hidden"
                    data-testid="inventory-watch-chat-file-input"
                  />
                  <button
                    onClick={() => chatFileInputRef.current?.click()}
                    disabled={chatBusy || chatImages.length >= 3}
                    className={`h-8 w-8 flex items-center justify-center rounded-md border transition-colors ${
                      isDark
                        ? 'border-slate-700 text-slate-400 hover:text-indigo-300 hover:border-indigo-500/50 disabled:opacity-40'
                        : 'border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300 disabled:opacity-40'
                    }`}
                    title="Attach a screenshot or photo (up to 3)"
                    data-testid="inventory-watch-chat-attach-btn"
                  >
                    <Paperclip size={13} />
                  </button>
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') sendChat(); }}
                    placeholder={chatImages.length > 0 ? 'Ask about the screenshot…' : 'Ask, tune, or attach a screenshot…'}
                    className={`h-8 text-xs flex-1 ${isDark ? 'bg-slate-900 border-slate-700 text-white placeholder:text-slate-500' : ''}`}
                    data-testid="inventory-watch-chat-input"
                  />
                  <Button
                    size="sm"
                    onClick={sendChat}
                    disabled={chatBusy || (!chatInput.trim() && chatImages.length === 0)}
                    className="h-8 px-2.5"
                    data-testid="inventory-watch-chat-send"
                  >
                    <Send size={13} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Inline extracted-inventory grid — replaces the previous
          third-party iframe embed. Retailers like Camping World
          frame-bust their listings, so we own the display: our own
          searchable table of every RV the AI extracted from uploads.
          Takes all remaining vertical space so the tab feels like a
          real inventory manager. */}
      <section
        className={`rounded-xl border flex-1 flex flex-col min-h-0 ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}
        data-testid="inventory-items-panel"
        style={{ minHeight: 'min(60vh, 520px)' }}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200/60 dark:border-slate-700/60 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Input
              value={itemsQuery}
              onChange={(e) => setItemsQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchItems(itemsQuery); }}
              placeholder="Search make, model, stock #, or VIN…"
              className={`h-8 pr-16 text-sm ${isDark ? 'bg-slate-900 border-slate-700 text-white placeholder:text-slate-500' : ''}`}
              data-testid="inventory-items-search"
            />
            <Button
              size="sm"
              onClick={() => fetchItems(itemsQuery)}
              className="absolute right-1 top-1 h-6 px-2 text-[11px]"
            >
              Search
            </Button>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={runDedupe}
            disabled={dedupeBusy || itemsTotal === 0}
            className="h-8 px-2 text-xs"
            title="Collapse duplicate units (same VIN/stock, same year/make/model)"
            data-testid="inventory-dedupe-btn"
          >
            {dedupeBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
            Dedupe
          </Button>
          <span className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {watchFilter ? `${watchFilter.items.length} of ${itemsTotal}` : itemsTotal} unit{(watchFilter ? watchFilter.items.length : itemsTotal) === 1 ? '' : 's'}
          </span>
        </div>
        {watchFilter && (
          <div
            className={`flex items-center gap-2 px-3 py-1.5 border-b text-xs ${isDark ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-200' : 'bg-indigo-50 border-indigo-200 text-indigo-800'}`}
            data-testid="inventory-watch-filter-banner"
          >
            <MessageSquare size={12} className="shrink-0" />
            <span className="flex-1 truncate">
              Inventory Watch results: <strong>“{watchFilter.label}”</strong>
            </span>
            <button
              onClick={() => setWatchFilter(null)}
              className={`shrink-0 font-bold px-2 py-0.5 rounded-full ${isDark ? 'bg-indigo-500/20 hover:bg-indigo-500/30' : 'bg-indigo-100 hover:bg-indigo-200'}`}
              data-testid="inventory-watch-filter-clear"
            >
              × Show all
            </button>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {itemsLoading && !watchFilter ? (
            <div className="p-8 text-center">
              <Loader2 className="animate-spin mx-auto" size={20} />
            </div>
          ) : (watchFilter ? watchFilter.items : items).length === 0 ? (
            <div className={`p-10 text-center text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {itemsQuery
                ? `No units match "${itemsQuery}".`
                : 'No inventory extracted yet. Upload a PDF, CSV, or spreadsheet above — the AI reads every RV inside.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className={`sticky top-0 z-[1] ${isDark ? 'bg-slate-800/95' : 'bg-slate-50/95'} backdrop-blur`}>
                <tr className={`text-[11px] uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  <th className="text-left px-3 py-2 font-semibold">Year / Make / Model</th>
                  <th className="text-left px-3 py-2 font-semibold hidden sm:table-cell">Class</th>
                  <th className="text-right px-3 py-2 font-semibold">Price</th>
                  <th className="text-right px-3 py-2 font-semibold hidden lg:table-cell">
                    <button
                      onClick={cycleDaysSort}
                      className={`inline-flex items-center gap-1 uppercase tracking-wider ${daysSort ? (isDark ? 'text-amber-300' : 'text-amber-700') : ''}`}
                      title="Sort by days on lot"
                      data-testid="inventory-days-sort"
                    >
                      Days {daysSort === 'desc' ? '↓' : daysSort === 'asc' ? '↑' : <ArrowUpDown size={10} className="opacity-50" />}
                    </button>
                  </th>
                  <th className="text-left px-3 py-2 font-semibold hidden md:table-cell">Stock / VIN</th>
                </tr>
              </thead>
              <tbody>
                {sortRows(watchFilter ? watchFilter.items : items).map((it) => {
                  // Map dealer stock-status codes → visible chips. Only
                  // the "deal pending" (SA) code is currently amber; the
                  // rest render as subtle grey pills so dealers can still
                  // see them but they don't compete with SA for attention.
                  const STATUS_MAP = {
                    SA: { label: 'Deal Pending', tone: 'amber' },
                    HD: { label: 'Hold', tone: 'blue' },
                    SO: { label: 'Sold', tone: 'slate' },
                    TR: { label: 'In Transit', tone: 'purple' },
                    IS: { label: 'In Stock', tone: 'green' },
                  };
                  const sc = it.status ? STATUS_MAP[it.status] : null;
                  const toneClasses = {
                    amber: isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-800',
                    blue: isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-800',
                    slate: isDark ? 'bg-slate-500/20 text-slate-300' : 'bg-slate-200 text-slate-700',
                    purple: isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-800',
                    green: isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-800',
                  };
                  // Build a public-listing URL. DuckDuckGo's ``!ducky``
                  // bang auto-redirects to the top result — so a
                  // ``site:{host} {stock}`` query lands the buyer
                  // directly on the exact listing page across any
                  // dealer/retailer, with no interstitial search page.
                  const publicListingUrl = (() => {
                    const base = dealer?.external_inventory_url;
                    if (!base) return null;
                    const key = it.stock_number || it.vin;
                    if (!key) return base;
                    try {
                      const host = new URL(base).hostname;
                      return `https://duckduckgo.com/?q=${encodeURIComponent(`!ducky site:${host} ${key}`)}`;
                    } catch {
                      const sep = base.includes('?') ? '&' : '?';
                      return `${base}${sep}q=${encodeURIComponent(key)}`;
                    }
                  })();
                  return (
                  <tr
                    key={it.id}
                    className={`border-t ${isDark ? 'border-slate-800 hover:bg-slate-800/40' : 'border-slate-100 hover:bg-slate-50'}`}
                    data-testid={`inventory-item-${it.id}`}
                  >
                    <td className="px-3 py-2">
                      <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} flex items-center gap-2 flex-wrap`}>
                        {publicListingUrl ? (
                          <a
                            href={publicListingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`hover:underline inline-flex items-center gap-1 ${isDark ? 'text-white hover:text-emerald-300' : 'text-slate-900 hover:text-emerald-700'}`}
                            title="Open on your public inventory site"
                            data-testid={`inventory-item-link-${it.id}`}
                          >
                            <span>{it.year || '—'} {it.make || ''} {it.model || ''}</span>
                            <ExternalLink size={11} className="opacity-60" />
                          </a>
                        ) : (
                          <span>{it.year || '—'} {it.make || ''} {it.model || ''}</span>
                        )}
                        {it.is_new ? (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${isDark ? 'bg-cyan-500/25 text-cyan-300' : 'bg-cyan-100 text-cyan-800'}`}
                            title="Added in the latest inventory snapshot"
                            data-testid={`inventory-new-chip-${it.id}`}
                          >
                            New
                          </span>
                        ) : null}
                        {sc ? (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${toneClasses[sc.tone]}`}
                            title={`${it.status} — ${sc.label}`}
                            data-testid={`inventory-status-chip-${it.id}`}
                          >
                            {sc.label}
                          </span>
                        ) : it.status ? (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${toneClasses.slate}`}
                            title={`Raw dealer status: ${it.status}`}
                          >
                            {it.status}
                          </span>
                        ) : null}
                      </div>
                      {(it.condition || it.length_ft || it.sleeps) ? (
                        <div className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {it.condition || ''}
                          {it.length_ft ? `${it.condition ? ' · ' : ''}${it.length_ft} ft` : ''}
                          {it.sleeps ? ` · sleeps ${it.sleeps}` : ''}
                        </div>
                      ) : null}
                    </td>
                    <td className={`px-3 py-2 hidden sm:table-cell ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      {it.class || '—'}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                      {it.list_price && it.price && Number(it.list_price) > Number(it.price) ? (
                        <span className={`block text-[10px] line-through ${isDark ? 'text-slate-500' : 'text-slate-400'}`} title="List price">
                          ${Number(it.list_price).toLocaleString()}
                        </span>
                      ) : null}
                      {it.price ? `$${Number(it.price).toLocaleString()}` : '—'}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono hidden lg:table-cell ${
                        it.days_on_lot != null && Number(it.days_on_lot) >= 90
                          ? (isDark ? 'text-amber-300' : 'text-amber-700')
                          : (isDark ? 'text-slate-400' : 'text-slate-500')
                      }`}
                      data-testid={`inventory-days-${it.id}`}
                    >
                      {it.days_on_lot != null ? it.days_on_lot : '—'}
                    </td>
                    <td className={`px-3 py-2 hidden md:table-cell text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {it.stock_number ? <div>#{it.stock_number}</div> : null}
                      {it.vin ? <div className="font-mono">{it.vin}</div> : null}
                      {it.lot_location ? (
                        <div className={`text-[10px] font-bold uppercase ${isDark ? 'text-sky-400' : 'text-sky-600'}`} title="Lot location" data-testid={`inventory-loc-${it.id}`}>
                          {it.lot_location}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {items.length > 0 ? (
          <p className={`text-[11px] px-3 py-2 border-t ${isDark ? 'border-slate-700 text-slate-500' : 'border-slate-100 text-slate-400'}`}>
            These units auto-match to leads in your Pipeline &mdash; look for the &ldquo;N fits&rdquo; chip on Kanban cards, and the <strong>Local Matches</strong> panel inside any lead.
          </p>
        ) : null}
      </section>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className={isDark ? 'bg-slate-900 text-white border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>Inventory Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>External Inventory URL</Label>
              <Input
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="https://your-inventory-system.com"
                className={`mt-1 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Optional — embed an existing inventory site alongside your uploaded files.
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowSettingsModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={saveExternalUrl} className="flex-1">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryManagerEmbed;
